import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RichTextDisplay } from "@/components/RichTextEditor";
import {
  BookOpen, Castle, Compass, Users, Shield, Sun, FlaskConical, Scroll, Hourglass,
  Search, ChevronLeft, LogIn, Sparkles, ChevronRight, Eye, EyeOff, Swords, Table2, List,
} from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50) || "section";
import "./PlayerWikiPage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Player-facing Wiki page — styled as an open tome / book spread.
 *
 * Pulls every entity flagged "all_players" for the active campaign and lays it out as
 * a single browsable reference. Works for:
 *   - Guest players (using the `guest_<campaignId>` localStorage token), or
 *   - Logged-in players / DMs (session cookie).
 *
 * Sections appear/disappear based on what the DM has revealed. Each entity reuses the
 * existing rich-text editor output so @mentions stay clickable inside descriptions.
 */

const SECTIONS = [
  { id: "lore",      label: "Lore",            icon: BookOpen,     endpoint: "lore",      titleField: "title", bodyField: "content",     entityType: "lore" },
  { id: "quests",    label: "Quests",          icon: Swords,       endpoint: "quests",    titleField: "title", bodyField: "description", entityType: "quests" },
  { id: "locations", label: "Towns & Places",  icon: Castle,       endpoint: "locations", titleField: "name",  bodyField: "description", imageField: "image_path",   entityType: "locations" },
  { id: "maps",      label: "Maps",            icon: Compass,      endpoint: "maps",      titleField: "name",  bodyField: "description", imageField: "storage_path", entityType: "maps" },
  { id: "npcs",      label: "People",          icon: Users,        endpoint: "npcs",      titleField: "name",  bodyField: "description", imageField: "image_url",    entityType: "npcs" },
  { id: "factions",  label: "Factions",        icon: Shield,       endpoint: "factions",  titleField: "name",  bodyField: "description", entityType: "factions" },
  { id: "deities",   label: "Deities",         icon: Sun,          endpoint: "deities",   titleField: "name",  bodyField: "description", imageField: "image_path",   entityType: "deities" },
  { id: "items",     label: "Items",           icon: FlaskConical, endpoint: "items",     titleField: "name",  bodyField: "description", imageField: "image_path",   entityType: "items" },
  { id: "handouts",  label: "Handouts",        icon: Scroll,       endpoint: "handouts",  titleField: "title", bodyField: "content",     entityType: "handouts" },
  { id: "tables",    label: "Tables",          icon: Table2,       endpoint: "tables",    titleField: "name",  bodyField: "description", entityType: "custom_tables" },
  { id: "timeline",  label: "Timeline",        icon: Hourglass,    endpoint: "timeline",  titleField: "title", bodyField: "description", entityType: "timeline_events" },
];

// Build a viewable URL from a stored asset path. Storage keys like
// "naturalone-chronicles/maps/abc.png" must be served via /api/files/{path};
// already-resolved http(s) URLs (e.g. AI portrait CDN) pass through.
const buildFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API}/files/${path}`;
};

// Quests use a different visibility scheme ("all" / "dm_only" / "specific") than
// the rest of the wiki ("all_players" / "dm_only"). Tables use `visible_to_players: bool`.
// Treat any of these as revealed.
const isRevealed = (e) => e?.visibility === "all_players" || e?.visibility === "all" || e?.is_shared === true || e?.visible_to_players === true;

const PlayerWikiPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const guestToken = typeof window !== "undefined" ? localStorage.getItem(`guest_${campaignId}`) : null;
  const headers = guestToken ? { "X-Guest-Token": guestToken } : {};

  const [counts, setCounts] = useState({});
  const [data, setData] = useState({});
  const [campaign, setCampaign] = useState(null);
  const [activeSection, setActiveSection] = useState("lore");
  const [activeEntity, setActiveEntity] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDm, setIsDm] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [justRevealed, setJustRevealed] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [backRefs, setBackRefs] = useState([]);
  const [backRefsLoading, setBackRefsLoading] = useState(false);
  // "On this page" table-of-contents (v2)
  const proseRef = useRef(null);
  const rightPageRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeHeading, setActiveHeading] = useState(null);
  const scrollToHeading = (id) => {
    setActiveHeading(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Fetch the "Just Revealed" feed once on load (and after a bulk share/hide).
  const fetchJustRevealed = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/guest/portal/${campaignId}/just-revealed`, { headers, withCredentials: true });
      setJustRevealed(r.data || []);
    } catch { setJustRevealed([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // Bulk reveal/hide all entries inside the active section. DM-only.
  const bulkVisibility = async (action) => {
    if (!isDm) return;
    const sec = SECTIONS.find(s => s.id === activeSection);
    if (!sec) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/bulk-visibility`,
        { section: sec.id, action },
        { withCredentials: true }
      );
      const verb = action === "reveal_all" ? "revealed" : "hidden";
      // Local re-fetch — simpler than mutating ~10 collections of state in place
      window.dispatchEvent(new CustomEvent("wiki-bulk-done", { detail: { count: res.data?.modified || 0, verb } }));
    } finally {
      setBulkBusy(false);
    }
  };

  // Fetch back-references for the active entity (Mentioned in panel)
  useEffect(() => {
    if (!activeEntity?.id) { setBackRefs([]); return; }
    const sec = SECTIONS.find(s => s.id === activeSection);
    if (!sec) return;
    let cancelled = false;
    setBackRefsLoading(true);
    axios.get(`${API}/entities/${sec.entityType}/${activeEntity.id}/back-references`, { withCredentials: true })
      .then(r => { if (!cancelled) setBackRefs(r.data || []); })
      .catch(() => { if (!cancelled) setBackRefs([]); })
      .finally(() => { if (!cancelled) setBackRefsLoading(false); });
    return () => { cancelled = true; };
  }, [activeEntity?.id, activeSection]);

  // Build the "On this page" TOC from the rendered article headings.
  useEffect(() => {
    if (!activeEntity) { setToc([]); setActiveHeading(null); return; }
    const t = setTimeout(() => {
      const root = proseRef.current;
      if (!root) { setToc([]); return; }
      const hs = Array.from(root.querySelectorAll("h1, h2, h3"));
      const items = hs.map((h, i) => {
        const text = (h.textContent || "").trim();
        if (!h.id) h.id = `wiki-h-${i}-${slugify(text)}`;
        return { id: h.id, text, level: Number(h.tagName.substring(1)) };
      }).filter(x => x.text);
      setToc(items);
      setActiveHeading(items[0]?.id || null);
    }, 80);
    return () => clearTimeout(t);
  }, [activeEntity?.id, activeSection]);

  // Scroll-spy: highlight the heading currently in view within the article pane.
  useEffect(() => {
    if (toc.length === 0) return;
    const root = rightPageRef.current || null;
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveHeading(visible[0].target.id);
    }, { root, rootMargin: "0px 0px -70% 0px", threshold: 0 });
    toc.forEach(h => { const el = document.getElementById(h.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [toc]);

  const fetchAll = useCallback(async () => {
    try {
      // Mode probe — tells us if the viewer is a DM (sees everything)
      let dmFlag = false;
      try {
        const m = await axios.get(`${API}/guest/portal/${campaignId}/wiki-mode`, { headers, withCredentials: true });
        dmFlag = !!m.data?.is_dm;
      } catch { /* default to player mode */ }
      setIsDm(dmFlag);

      // Campaign meta — try authenticated first; guests will 401 silently and we fall back.
      try {
        const c = await axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true });
        setCampaign(c.data);
      } catch {
        try {
          const c = await axios.get(`${API}/guest/portal/${campaignId}`, { headers, withCredentials: true });
          setCampaign(c.data.campaign);
        } catch { /* still no campaign meta */ }
      }
      // Counts (DM gets { revealed, total }, players get plain numbers)
      const sum = await axios.get(`${API}/guest/portal/${campaignId}/wiki-summary`, { headers, withCredentials: true });
      setCounts(sum.data || {});
      // Default to the first section with entries
      const firstWith = SECTIONS.find(s => {
        const key = s.endpoint === "timeline" ? "timeline_events" : s.endpoint;
        const v = sum.data?.[key];
        const n = typeof v === "object" ? (dmFlag ? v.total : v.revealed) : v;
        return (n || 0) > 0;
      });
      if (firstWith) setActiveSection(firstWith.id);
      // Lazy-fetch each section's content
      const results = {};
      for (const s of SECTIONS) {
        try {
          const r = await axios.get(`${API}/guest/portal/${campaignId}/${s.endpoint}`, { headers, withCredentials: true });
          results[s.id] = r.data || [];
        } catch {
          results[s.id] = [];
        }
      }
      setData(results);
      setLoading(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError("You don't have access to this campaign yet. Ask your DM for an invite link.");
      } else {
        setError("Couldn't load the wiki right now. Try again in a moment.");
      }
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    fetchAll();
    fetchJustRevealed();
  }, [fetchAll, fetchJustRevealed]);

  // After a bulk share/hide finishes (toast event), refresh everything
  useEffect(() => {
    const handler = (e) => {
      fetchAll();
      fetchJustRevealed();
    };
    window.addEventListener("wiki-bulk-done", handler);
    return () => window.removeEventListener("wiki-bulk-done", handler);
  }, [fetchAll, fetchJustRevealed]);

  // DM-only: flip visibility on the currently-selected entity and refresh just that section
  const toggleVisibility = async (entity, section) => {
    if (!isDm || !entity?.id) return;
    setTogglingId(entity.id);
    try {
      const res = await axios.put(
        `${API}/entities/${section.entityType}/${entity.id}/quick-visibility`,
        {},
        { withCredentials: true }
      );
      const newVis = res.data?.visibility || (isRevealed(entity) ? "dm_only" : "all_players");
      const visibleFlag = res.data?.visible_to_players !== undefined
        ? !!res.data.visible_to_players
        : (newVis === "all_players" || newVis === "all");
      // Patch the in-memory list and the active entity
      setData(prev => {
        const list = (prev[section.id] || []).map(e =>
          e.id === entity.id ? { ...e, visibility: newVis, is_shared: newVis === "all_players", visible_to_players: visibleFlag } : e
        );
        return { ...prev, [section.id]: list };
      });
      setActiveEntity(prev => prev && prev.id === entity.id
        ? { ...prev, visibility: newVis, is_shared: newVis === "all_players", visible_to_players: visibleFlag }
        : prev
      );
      // Refresh counts so the sidebar tallies update
      const sum = await axios.get(`${API}/guest/portal/${campaignId}/wiki-summary`, { headers, withCredentials: true });
      setCounts(sum.data || {});
      toast.success(newVis === "all_players" ? "Shared with players" : "Hidden from players");
    } catch (e) {
      toast.error("Couldn't update visibility. Try again.");
    } finally {
      setTogglingId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const entriesFor = (section) => {
    const list = data[section.id] || [];
    if (!q) return list;
    return list.filter(e => {
      const title = (e[section.titleField] || "").toLowerCase();
      const body = (typeof e[section.bodyField] === "string" ? e[section.bodyField] : "").toLowerCase();
      // Tables — also match cell content
      if (section.id === "tables") {
        const cellsText = (e.rows || []).flat().join(" ").toLowerCase();
        const colsText  = (e.columns || []).join(" ").toLowerCase();
        return title.includes(q) || body.includes(q) || cellsText.includes(q) || colsText.includes(q);
      }
      return title.includes(q) || body.includes(q);
    });
  };

  const totalRevealed = useMemo(() => {
    return Object.values(counts).reduce((acc, n) => {
      if (typeof n === "object" && n) return acc + (Number(n.revealed) || 0);
      return acc + (Number(n) || 0);
    }, 0);
  }, [counts]);
  const totalAll = useMemo(() => {
    if (!isDm) return totalRevealed;
    return Object.values(counts).reduce((acc, n) => {
      if (typeof n === "object" && n) return acc + (Number(n.total) || 0);
      return acc + (Number(n) || 0);
    }, 0);
  }, [counts, isDm, totalRevealed]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-3 animate-pulse" />
          <p className="text-sm text-[#A1A1AA] font-['Cormorant_Garamond'] italic text-lg tracking-wide">Opening the tome…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-card border border-border rounded-lg p-8" data-testid="wiki-error">
          <Eye className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h1 className="font-serif text-xl text-foreground mb-2">Nothing to see here yet</h1>
          <p className="text-sm text-muted-foreground mb-5">{error}</p>
          <Button onClick={() => navigate("/login")} className="gap-2" data-testid="wiki-error-login">
            <LogIn className="w-4 h-4" /> Sign In
          </Button>
        </div>
      </div>
    );
  }

  const activeSec = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];
  const list = entriesFor(activeSec);
  const ActiveIcon = activeSec.icon;

  return (
    <div className="min-h-screen bg-[#09090b] text-[#F3F4F6] relative overflow-x-hidden" data-testid="player-wiki-page">
      {/* Faint leather desk wash */}
      <div
        className="fixed inset-0 pointer-events-none opacity-30 mix-blend-overlay"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 10%, rgba(139,69,19,0.18), transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(80,40,15,0.22), transparent 60%)",
        }}
      />

      {/* Slim chrome bar: back / search / DM badge — sits outside the book */}
      <div className="relative z-30 flex items-center gap-3 px-3 sm:px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(guestToken ? `/campaign/${campaignId}/player-portal` : -1)}
          className="text-[#D4AF37] hover:text-[#FDE08A] hover:bg-white/5"
          data-testid="wiki-back"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0 hidden md:flex items-center gap-2 text-[#D4AF37]/80">
          <BookOpen className="w-4 h-4 flex-shrink-0" />
          <span className="font-['Cormorant_Garamond'] italic truncate" data-testid="wiki-campaign-name">
            {campaign?.name || "Campaign Chronicle"}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/50">
            {isDm
              ? <>· {totalRevealed} of {totalAll} shared</>
              : <>· {totalRevealed} entries revealed</>
            }
          </span>
        </div>
        <div className="flex-1 md:hidden" />
        {isDm && (
          <Badge
            className="tome-dm-note text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 hover:bg-[#FEF3C7]"
            data-testid="wiki-dm-badge"
          >
            DM Preview
          </Badge>
        )}
      </div>

      {/* THE BOOK — two-page spread */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-2 sm:px-4 pb-8">
        <div
          className="tome-book relative w-full h-[calc(100vh-110px)] md:h-[calc(100vh-130px)] min-h-[560px] flex flex-col md:flex-row tome-spread shadow-[0_30px_60px_rgba(0,0,0,0.6),inset_0_0_120px_rgba(139,69,19,0.06)] rounded-sm"
          data-testid="wiki-book"
        >
          {/* Grain overlay */}
          <div className="tome-grain absolute inset-0" aria-hidden="true" />
          {/* Centre gutter shadow (desktop only) */}
          <div className="tome-gutter hidden md:block" aria-hidden="true" />
          {/* Red ribbon bookmark */}
          <div className="tome-ribbon hidden md:block" aria-hidden="true" />

          {/* Spine: leather section tabs (desktop) */}
          <nav
            className="hidden md:flex absolute left-0 top-10 -translate-x-[calc(100%-2px)] flex-col gap-1.5 z-30"
            data-testid="wiki-nav"
            aria-label="Wiki sections"
          >
            {SECTIONS.map(s => {
              const Icon = s.icon;
              const key = s.endpoint === "timeline" ? "timeline_events" : s.endpoint;
              const raw = counts[key];
              const revealed = typeof raw === "object" ? (raw?.revealed ?? 0) : (raw ?? 0);
              const total = typeof raw === "object" ? (raw?.total ?? 0) : (raw ?? 0);
              const fallback = data[s.id]?.length || 0;
              const active = activeSection === s.id;
              const visibleCount = isDm ? `${revealed}/${total || fallback}` : (revealed || fallback);
              return (
                <button
                  key={s.id}
                  onClick={() => { setActiveSection(s.id); setActiveEntity(null); }}
                  className={`tome-tab ${active ? "is-active" : ""} group flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-l-sm text-sm ${active ? "w-[150px]" : "w-[58px] hover:w-[150px]"}`}
                  data-testid={`wiki-section-${s.id}`}
                  aria-current={active ? "page" : undefined}
                  aria-label={s.label}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  <span className={`flex-1 text-left truncate ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>{s.label}</span>
                  <span className={`text-[10px] tabular-nums ${active ? "opacity-90" : "opacity-0 group-hover:opacity-70"} transition-opacity`}>{visibleCount}</span>
                </button>
              );
            })}
          </nav>

          {/* Mobile section strip */}
          <div className="md:hidden flex items-center gap-1.5 overflow-x-auto px-3 py-2 bg-[#1A1512] border-b border-[#D4AF37]/30 tome-scroll-hide flex-shrink-0">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              const key = s.endpoint === "timeline" ? "timeline_events" : s.endpoint;
              const raw = counts[key];
              const revealed = typeof raw === "object" ? (raw?.revealed ?? 0) : (raw ?? 0);
              const total = typeof raw === "object" ? (raw?.total ?? 0) : (raw ?? 0);
              const fallback = data[s.id]?.length || 0;
              const active = activeSection === s.id;
              const visibleCount = isDm ? `${revealed}/${total || fallback}` : (revealed || fallback);
              return (
                <button
                  key={s.id}
                  onClick={() => { setActiveSection(s.id); setActiveEntity(null); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs whitespace-nowrap border transition-colors ${
                    active
                      ? "bg-[#D4AF37]/15 text-[#FDE08A] border-[#D4AF37]/40"
                      : "bg-transparent text-[#D4AF37]/70 border-[#D4AF37]/15 hover:text-[#FDE08A]"
                  }`}
                  data-testid={`wiki-section-mobile-${s.id}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {s.label}
                  <span className="text-[9px] opacity-70 tabular-nums">({visibleCount})</span>
                </button>
              );
            })}
          </div>

          {/* LEFT PAGE — section header + entry list */}
          <section
            className="relative z-10 w-full md:w-1/2 h-1/2 md:h-full overflow-y-auto p-5 sm:p-8 md:p-10 lg:p-12 tome-scroll border-b md:border-b-0 md:border-r border-[#D4AF37]/15"
            data-testid="wiki-left-page"
          >
            {/* Recto/verso page metadata */}
            <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans">
              <span>Index · {activeSec.label}</span>
              <span className="tabular-nums">{list.length} {list.length === 1 ? "entry" : "entries"}</span>
            </div>

            <h2
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#2C2621] leading-tight mt-1 mb-1 flex items-baseline gap-3"
              data-testid="wiki-section-title"
            >
              <ActiveIcon className="w-7 h-7 text-[#A98C28] self-center -translate-y-0.5" strokeWidth={1.5} />
              {activeSec.label}
            </h2>
            <div className="tome-divider"><span className="tome-divider-glyph" aria-hidden="true">❦</span></div>

            {/* Bulk Share / Hide — DM only, one row per section */}
            {isDm && list.length > 0 && (
              <div className="flex items-center gap-2 mb-4" data-testid="wiki-bulk-controls">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() => bulkVisibility("reveal_all")}
                  className="h-7 text-[10px] uppercase tracking-wider text-emerald-700 hover:bg-emerald-100/60 hover:text-emerald-900 font-sans gap-1.5 px-2"
                  data-testid="wiki-bulk-reveal"
                >
                  <Eye className="w-3 h-3" /> Reveal all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() => bulkVisibility("hide_all")}
                  className="h-7 text-[10px] uppercase tracking-wider text-stone-700 hover:bg-stone-100/60 hover:text-stone-900 font-sans gap-1.5 px-2"
                  data-testid="wiki-bulk-hide"
                >
                  <EyeOff className="w-3 h-3" /> Hide all
                </Button>
                <span className="text-[10px] font-sans text-stone-500 italic">Bulk-toggle every {activeSec.label.toLowerCase()} entry.</span>
              </div>
            )}

            {/* Just Revealed feed — shows for players, collapsible */}
            {!isDm && justRevealed.length > 0 && activeSection === "lore" && (
              <details className="mb-5 group" data-testid="wiki-just-revealed" open>
                <summary className="cursor-pointer select-none flex items-center gap-2 font-['Cormorant_Garamond'] italic text-[#A98C28] text-base hover:text-[#D4AF37]">
                  <Sparkles className="w-4 h-4" />
                  Just Revealed
                  <span className="text-[10px] font-sans uppercase tracking-wider opacity-70">past 7 days</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-auto transition-transform group-open:rotate-90" />
                </summary>
                <ul className="mt-3 space-y-1.5 ml-1">
                  {justRevealed.slice(0, 8).map(r => (
                    <li key={`${r.section_id}-${r.id}`}>
                      <button
                        onClick={() => {
                          setActiveSection(r.section_id);
                          const sec = SECTIONS.find(s => s.id === r.section_id);
                          const list = data[r.section_id] || [];
                          const found = list.find(x => x.id === r.id);
                          if (found && sec) setActiveEntity({ ...found, id: r.id });
                        }}
                        className="w-full flex items-center gap-2 text-left text-sm font-['EB_Garamond'] text-[#2C2621] hover:text-[#A98C28] group/item"
                        data-testid={`wiki-just-revealed-${r.id}`}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#A98C28] group-hover/item:bg-[#D4AF37] flex-shrink-0" />
                        <span className="truncate">{r.title}</span>
                        <span className="text-[10px] font-sans uppercase tracking-wider opacity-50 ml-auto">{r.section_id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="tome-divider mt-3"><span className="tome-divider-glyph" aria-hidden="true">·</span></div>
              </details>
            )}

            {/* Search line */}
            <div className="mb-5 relative">
              <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[#A98C28]/70 pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this chronicle…"
                className="tome-search pl-7"
                data-testid="wiki-search"
                aria-label="Search the wiki"
              />
            </div>

            {/* Entry list / empty state */}
            {list.length === 0 ? (
              isDm ? (
                <div className="text-center py-12 text-[#5C5346]" data-testid="wiki-empty">
                  <ActiveIcon className="w-10 h-10 text-[#A98C28]/40 mx-auto mb-3" strokeWidth={1.2} />
                  <p className="font-['EB_Garamond'] italic text-base">
                    {q
                      ? <>No {activeSec.label.toLowerCase()} match &ldquo;<span className="text-[#2C2621]">{q}</span>&rdquo;.</>
                      : <>Nothing recorded under {activeSec.label.toLowerCase()} — yet.</>
                    }
                  </p>
                </div>
              ) : totalRevealed === 0 ? (
                <div className="text-center py-10 px-4" data-testid="wiki-empty-welcome">
                  <Sparkles className="w-10 h-10 text-[#A98C28] mx-auto mb-3" strokeWidth={1.2} />
                  <h3 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2621] mb-2 italic">Your chronicle begins soon.</h3>
                  <p className="font-['EB_Garamond'] text-base text-[#5C5346] max-w-sm mx-auto leading-relaxed">
                    Your storyteller has not yet inscribed these pages. Once they unveil places, people, lore or maps, the tome will fill of its own accord.
                  </p>
                </div>
              ) : (
                <div className="text-center py-12 text-[#5C5346]" data-testid="wiki-empty">
                  <ActiveIcon className="w-10 h-10 text-[#A98C28]/40 mx-auto mb-3" strokeWidth={1.2} />
                  <p className="font-['EB_Garamond'] italic text-base">
                    {q
                      ? <>No {activeSec.label.toLowerCase()} match &ldquo;<span className="text-[#2C2621]">{q}</span>&rdquo;.</>
                      : <>The chronicle is silent on {activeSec.label.toLowerCase()} for now.</>
                    }
                  </p>
                </div>
              )
            ) : (
              <ul className="space-y-0">
                {list.map((e, idx) => {
                  const id = e.id || e._id || e[activeSec.titleField];
                  const title = e[activeSec.titleField] || "Untitled";
                  const summary = activeSec.id === "tables"
                    ? (e.description || `${(e.columns || []).length} cols · ${(e.rows || []).length} rows`)
                    : (e.short_description || e.summary || (typeof e[activeSec.bodyField] === "string" ? e[activeSec.bodyField].replace(/<[^>]*>/g, "").slice(0, 140) : ""));
                  const img = activeSec.imageField ? e[activeSec.imageField] : null;
                  const imgUrl = buildFileUrl(img);
                  const selected = activeEntity?.id === id;
                  const shared = isRevealed(e);
                  return (
                    <li key={id}>
                      <button
                        onClick={() => setActiveEntity({ ...e, id })}
                        className={`group w-full flex items-start gap-4 py-3 px-2 -mx-2 text-left rounded-sm transition-all duration-200 ${
                          selected
                            ? "bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]/30"
                            : "hover:bg-[#8B4513]/[0.05]"
                        }`}
                        data-testid={`wiki-entry-${id}`}
                        data-revealed={shared ? "yes" : "no"}
                      >
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={title}
                            className="w-14 h-14 rounded-sm object-cover border border-[#D4AF37]/40 shadow-sm flex-shrink-0"
                            style={{ filter: "sepia(0.25)" }}
                            crossOrigin="use-credentials"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center flex-shrink-0">
                            <ActiveIcon className="w-5 h-5 text-[#A98C28]/70" strokeWidth={1.4} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-['Cormorant_Garamond'] text-xl leading-tight truncate ${isDm && !shared ? "text-[#5C5346]/70" : "text-[#2C2621]"}`}>
                              {title}
                            </h3>
                            {isDm && !shared && (
                              <span
                                className="tome-dm-note text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0"
                                data-testid={`wiki-entry-badge-${id}`}
                              >
                                Hidden
                              </span>
                            )}
                          </div>
                          {summary && (
                            <p className="font-['EB_Garamond'] text-sm text-[#5C5346] line-clamp-2 mt-0.5 italic">{summary}</p>
                          )}
                        </div>
                        <ChevronRight className={`w-4 h-4 text-[#A98C28]/40 self-center flex-shrink-0 transition-transform ${selected ? "translate-x-0.5 text-[#A98C28]" : "group-hover:translate-x-0.5"}`} />
                      </button>
                      {idx < list.length - 1 && (
                        <div className="border-b border-[#D4AF37]/15 ml-[64px]" aria-hidden="true" />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* RIGHT PAGE — detail */}
          <section
            ref={rightPageRef}
            className="relative z-10 w-full md:w-1/2 h-1/2 md:h-full overflow-y-auto p-5 sm:p-8 md:p-10 lg:p-14 tome-scroll"
            data-testid="wiki-right-page"
          >
            {activeEntity ? (
              <article data-testid="wiki-detail" className="relative lg:flex lg:gap-8 lg:items-start">
                <div className="min-w-0 lg:flex-1">
                <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans">
                  <span>{activeSec.label}</span>
                  {isDm && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleVisibility(activeEntity, activeSec)}
                      disabled={togglingId === activeEntity.id}
                      className={`h-7 px-2 text-[10px] tracking-wider uppercase font-bold rounded ${
                        isRevealed(activeEntity)
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
                          : "tome-dm-note hover:bg-[#FEF3C7]"
                      }`}
                      data-testid="wiki-detail-share-toggle"
                    >
                      {isRevealed(activeEntity)
                        ? <><Eye className="w-3 h-3 mr-1" /> Shared</>
                        : <><EyeOff className="w-3 h-3 mr-1" /> Share</>
                      }
                    </Button>
                  )}
                </div>

                <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#2C2621] leading-[1.1] mb-1" data-testid="wiki-detail-title">
                  {activeEntity[activeSec.titleField]}
                </h1>

                {(activeEntity.location_type || activeEntity.faction || activeEntity.npc_type || activeEntity.role || activeEntity.domain || activeEntity.alignment) && (
                  <p className="font-['EB_Garamond'] italic text-[#A98C28] text-base mb-3">
                    {[activeEntity.location_type, activeEntity.faction, activeEntity.npc_type, activeEntity.role, activeEntity.domain, activeEntity.alignment].filter(Boolean).join(" · ")}
                  </p>
                )}

                <div className="tome-divider"><span className="tome-divider-glyph" aria-hidden="true">❦</span></div>

                {isDm && !isRevealed(activeEntity) && (
                  <div className="mb-4 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2 font-sans" data-testid="wiki-detail-hidden-banner">
                    <EyeOff className="w-3.5 h-3.5 flex-shrink-0" />
                    Players cannot see this entry yet.
                  </div>
                )}

                {activeSec.imageField && activeEntity[activeSec.imageField] && (
                  <figure className="float-right ml-5 mb-3 mt-1 w-44 md:w-56">
                    <img
                      src={buildFileUrl(activeEntity[activeSec.imageField])}
                      alt={activeEntity[activeSec.titleField] || ""}
                      className="w-full rounded-sm border border-[#D4AF37]/40 shadow-md"
                      style={{ filter: "sepia(0.2)" }}
                      crossOrigin="use-credentials"
                    />
                    <figcaption className="text-center mt-1 font-['EB_Garamond'] italic text-xs text-[#5C5346]">
                      — {activeEntity[activeSec.titleField]} —
                    </figcaption>
                  </figure>
                )}

                {activeSec.id === "tables" ? (
                  <div className="tome-prose">
                    {activeEntity.description && (
                      <p className="font-['EB_Garamond'] italic text-[#5C5346] mb-4">{activeEntity.description}</p>
                    )}
                    <div className="rounded-md overflow-hidden border-2 border-[#8a6a2e]/60 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]" data-testid="wiki-table-grid">
                      <div
                        className="overflow-auto max-h-[65vh]"
                        style={{
                          backgroundColor: "#f4e9c9",
                          backgroundImage:
                            "radial-gradient(circle at 20% 30%, rgba(168,128,60,0.10) 0, transparent 60%)," +
                            "radial-gradient(circle at 80% 70%, rgba(120,80,30,0.10) 0, transparent 55%)",
                        }}
                      >
                        <table className="w-full border-collapse font-['EB_Garamond'] text-[#3a2a12]">
                          <thead>
                            <tr className="bg-[#8a6a2e]/15">
                              {(activeEntity.columns || []).map((col, cIdx) => (
                                <th key={cIdx} className="border border-[#8a6a2e]/40 px-3 py-2 text-left text-sm font-bold uppercase tracking-wide">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(activeEntity.rows || []).length === 0 ? (
                              <tr>
                                <td colSpan={(activeEntity.columns || []).length || 1} className="text-center italic text-[#3a2a12]/60 py-6 border border-[#8a6a2e]/30">
                                  This table is empty.
                                </td>
                              </tr>
                            ) : (
                              (activeEntity.rows || []).map((row, rIdx) => (
                                <tr key={rIdx} className={rIdx % 2 === 0 ? "bg-[#dfe9c7]/60" : ""}>
                                  {row.map((cell, cIdx) => (
                                    <td key={cIdx} className="border border-[#8a6a2e]/25 px-3 py-2 text-sm align-top whitespace-pre-wrap">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : activeEntity[activeSec.bodyField] ? (
                  <div className="tome-prose" ref={proseRef}>
                    <RichTextDisplay content={activeEntity[activeSec.bodyField]} />
                  </div>
                ) : (
                  <p className="font-['EB_Garamond'] italic text-[#5C5346] text-base">No further details have been recorded.</p>
                )}

                <div className="clear-both" />

                {/* Wiki back-references — "Mentioned in" panel */}
                {backRefs.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-[#D4AF37]/25" data-testid="wiki-back-refs">
                    <h3 className="font-['Cormorant_Garamond'] italic text-[#A98C28] text-lg mb-3 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Mentioned in
                      <span className="text-[10px] font-sans uppercase tracking-wider opacity-70">{backRefs.length} {backRefs.length === 1 ? "entry" : "entries"}</span>
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {backRefs.map(r => (
                        <li key={`${r.collection}-${r.id}`}>
                          <button
                            onClick={() => {
                              if (!r.section_id) return; // DM-only collections (session_logs) — not navigable here
                              setActiveSection(r.section_id);
                              const list = data[r.section_id] || [];
                              const found = list.find(x => x.id === r.id);
                              if (found) setActiveEntity({ ...found, id: r.id });
                            }}
                            disabled={!r.section_id}
                            className="w-full text-left flex items-center gap-2 text-sm font-['EB_Garamond'] text-[#2C2621] hover:text-[#A98C28] disabled:cursor-default group/ref"
                            data-testid={`wiki-back-ref-${r.id}`}
                          >
                            <span className="text-[#A98C28]/70 group-hover/ref:text-[#A98C28]">·</span>
                            <span className="truncate underline decoration-dotted decoration-[#D4AF37]/40 underline-offset-2 group-hover/ref:decoration-[#D4AF37]">{r.title}</span>
                            <span className="text-[10px] font-sans uppercase tracking-wider opacity-50 ml-auto">{(r.section_id || r.collection).replace("_", " ")}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {backRefsLoading && (
                  <div className="mt-8 text-xs font-sans text-stone-500 italic">Scanning the rest of the chronicle…</div>
                )}

                <div className="tome-divider mt-10"><span className="tome-divider-glyph" aria-hidden="true">✦ ❦ ✦</span></div>
                </div>

                {/* On this page — TOC (v2) */}
                {toc.length > 0 && (
                  <aside className="hidden lg:block w-52 shrink-0 sticky top-0 self-start pt-1" data-testid="wiki-on-this-page">
                    <div className="rounded-sm border border-[#D4AF37]/25 bg-[#F5ECD7]/50 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#A98C28] font-sans mb-2">
                        <List className="w-3 h-3" /> On this page
                      </div>
                      <ul className="space-y-1 border-l border-[#D4AF37]/25">
                        {toc.map((h) => (
                          <li key={h.id} style={{ paddingLeft: `${(h.level - 1) * 10}px` }}>
                            <button
                              onClick={() => scrollToHeading(h.id)}
                              data-testid={`wiki-toc-${h.id}`}
                              className={`-ml-px pl-2 border-l text-left w-full text-sm font-['EB_Garamond'] leading-snug transition-colors ${
                                activeHeading === h.id
                                  ? "border-[#8B4513] text-[#8B4513] font-semibold"
                                  : "border-transparent text-[#5C5346] hover:text-[#A98C28]"
                              }`}
                            >
                              {h.text}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </aside>
                )}
              </article>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <Sparkles className="w-10 h-10 text-[#A98C28]/60 mb-4" strokeWidth={1.2} />
                <p className="font-['Cormorant_Garamond'] italic text-2xl text-[#2C2621]">Choose an entry to read more.</p>
                <p className="font-['EB_Garamond'] text-sm text-[#5C5346] mt-2 max-w-xs">
                  Pick a title from the index opposite, and its page will appear here.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default PlayerWikiPage;
